import React, {FC, useState, useEffect} from 'react'
import {showSwal} from '../../../../ui-kit'
import {useParams, useNavigate} from 'react-router-dom'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Form, Button, Spinner} from 'react-bootstrap'
import Select, {MultiValue} from 'react-select'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {Grouping, EconomyOption} from '../groupings/core/_models'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import {
  getGroupingById,
  getGroupingOptions,
  createGrouping,
  updateGroupingById,
} from '../groupings/core/_requests'
import GroupingsDetailView from './components/GroupingsDetailView'
import { getISO2ByISO3 } from '../../../libs/misc'



const GroupingsDetailPage: FC<{subject: string}> = ({subject}) => {
  const {idGrouping} = useParams<{idGrouping: string}>()
  const navigate = useNavigate()
  const axiosPrivate = useAxiosPrivate()
  const ability = useAbility(AbilityContext)

  const isAddMode = !idGrouping
  const isDetailMode = !!idGrouping
  const [isEditMode, setIsEditMode] = useState(isAddMode)

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [economyOptions, setEconomyOptions] = useState<EconomyOption[]>([])

  const [groupingDetail, setGroupingDetail] = useState<Grouping>({
    grp: '',
    title: '',
    economies: '',
    customs_union: 0,
    diplay_multiple: 0,
  })

  // Load options (economies list)
  useEffect(() => {
    const loadOptionsAndDetail = async () => {
      setIsLoading(true)
      try {
        const optionsRes = await getGroupingOptions(axiosPrivate)
        const econOptions = optionsRes.data.data.economies || []
        setEconomyOptions(econOptions)

        if (isDetailMode && idGrouping) {
          const detailRes = await getGroupingById(axiosPrivate, idGrouping)
          const detailData = detailRes.data.data
          if (detailData) {
            setGroupingDetail({
              grp: detailData.grp || '',
              title: detailData.title || '',
              economies: detailData.economies || '',
              customs_union: detailData.customs_union ?? 0,
              diplay_multiple: detailData.diplay_multiple ?? 0,
            })
          }
        }
      } catch (err) {
        console.error('Failed to load groupings page options or detail:', err)
        showSwal('Error', 'Failed to load details.', 'error')
      } finally {
        setIsLoading(false)
      }
    }

    loadOptionsAndDetail()
  }, [idGrouping])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {name, value} = e.target
    setGroupingDetail((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleEconomiesChange = (selected: MultiValue<EconomyOption>) => {
    const economiesString = selected.map((opt) => opt.value).join(';')
    setGroupingDetail((prev) => ({
      ...prev,
      economies: economiesString,
    }))
  }

  const selectedEconomies = groupingDetail.economies
    ? groupingDetail.economies
        .split(';')
        .map((code) => {
          const iso2 = getISO2ByISO3(code)
          const option = economyOptions.find((opt) => opt.value === code)
          return option ? option : {value: code, label: code}
        })
        .filter(Boolean)
    : []

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!groupingDetail.grp.trim() || !groupingDetail.title.trim()) {
      showSwal('Warning', 'Group Code and Title are required.', 'warning')
      return
    }

    showSwal({
      title: `Are you sure you want to ${isAddMode ? 'create' : 'update'} this grouping?`,
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return

      setIsLoadingButton(true)
      const payload = {
        grp: groupingDetail.grp.trim(),
        title: groupingDetail.title.trim(),
        economies: groupingDetail.economies,
        customs_union: groupingDetail.customs_union,
        diplay_multiple: groupingDetail.diplay_multiple,
      }

      if (isAddMode) {
        createGrouping(axiosPrivate, payload)
          .then((res) => {
            if (res.status === 201) {
              showSwal('Data created', undefined, 'success').then(() => {
                navigate('/groupings')
              })
            }
          })
          .catch((err) => {
            console.error('Failed to create grouping:', err)
            showSwal('Error', 'Failed to create grouping.', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      } else {
        updateGroupingById(axiosPrivate, idGrouping, payload)
          .then((res) => {
            if (res.status === 200) {
              showSwal('Data updated', undefined, 'success').then(() => {
                navigate('/groupings')
              })
            }
          })
          .catch((err) => {
            console.error('Failed to update grouping:', err)
            showSwal('Error', 'Failed to update grouping.', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      }
    })
  }

  const handleCancel = () => {
    showSwal({
      title: 'Are you sure you want to cancel?',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        if (isAddMode) {
          navigate('/groupings')
        } else {
          setIsEditMode(false)
          window.location.reload()
        }
      }
    })
  }

  const isFormValid = groupingDetail.grp.trim() && groupingDetail.title.trim()

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Groupings',
            path: '/groupings',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {isAddMode ? 'Add Grouping' : isEditMode ? `Edit Grouping: ${groupingDetail.grp}` : `Grouping Detail: ${groupingDetail.grp}`}
      </PageTitle>

      <div className='GroupingsDetailPage'>
        <Row className='mb-5'>
          <Col>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>
                        {isAddMode ? 'Add New Grouping' : isEditMode ? 'Edit Grouping Details' : 'Grouping Detail'}
                      </h3>
                    </div>
                    {ability.can('update', subject) && isDetailMode && !isEditMode && (
                      <Button
                        className='btn btn-primary d-flex gap-4 align-items-center'
                        onClick={() => setIsEditMode(true)}
                      >
                        <span className='fa fa-pencil'></span>
                        Edit
                      </Button>
                    )}
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!isLoading ? (
                  isEditMode ? (
                    <Form onSubmit={handleSubmit} className='form-groupings'>
                      <h3>General Information</h3>
                      <div className='row mb-4'>
                        <div className='col-md-6 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>
                            Group Code
                          </Form.Label>
                          <Form.Control
                            placeholder='e.g. EUN, ASEAN, APTA'
                            type='text'
                            name='grp'
                            value={groupingDetail.grp}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className='col-md-6 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>
                            Title
                          </Form.Label>
                          <Form.Control
                            placeholder='e.g. European Union, ASEAN'
                            type='text'
                            name='title'
                            value={groupingDetail.title}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      <hr />
                      <h3>Participation & Display</h3>
                      <div className='row mb-4'>
                        <div className='col-md-12 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>
                            Economies
                          </Form.Label>
                          <Select
                            isMulti
                            required
                            name='economies'
                            placeholder='Select economies...'
                            options={economyOptions}
                            value={selectedEconomies}
                            onChange={handleEconomiesChange}
                            styles={{
                              valueContainer: (base) => ({
                                ...base,
                                height: '3.27rem',
                                overflowY: 'auto',
                              }),
                            }}
                          />
                        </div>
                      </div>

                      <div className='row'>
                        <div className='col-md-6 mb-5 d-flex flex-column justify-content-center'>
                          <Form.Label className='text-primary me-0 ps-0 mb-2'>
                            Customs Union
                          </Form.Label>
                          <div className='d-flex align-items-center gap-4' style={{minHeight: '38px'}}>
                            <Form.Check
                              type='radio'
                              id='customs_union-yes'
                              name='customs_union'
                              label='Yes'
                              checked={groupingDetail.customs_union === 1}
                              onChange={() =>
                                setGroupingDetail((prev) => ({...prev, customs_union: 1}))
                              }
                              className='d-flex align-items-center gap-2'
                            />
                            <Form.Check
                              type='radio'
                              id='customs_union-no'
                              name='customs_union'
                              label='No'
                              checked={groupingDetail.customs_union === 0}
                              onChange={() =>
                                setGroupingDetail((prev) => ({...prev, customs_union: 0}))
                              }
                              className='d-flex align-items-center gap-2'
                            />
                          </div>
                        </div>

                        <div className='col-md-6 mb-5 d-flex flex-column justify-content-center'>
                          <Form.Label className='text-primary me-0 ps-0 mb-2'>
                            Display Multiple
                          </Form.Label>
                          <div className='d-flex align-items-center gap-4' style={{minHeight: '38px'}}>
                            <Form.Check
                              type='radio'
                              id='diplay_multiple-yes'
                              name='diplay_multiple'
                              label='Yes'
                              checked={groupingDetail.diplay_multiple === 1}
                              onChange={() =>
                                setGroupingDetail((prev) => ({...prev, diplay_multiple: 1}))
                              }
                              className='d-flex align-items-center gap-2'
                            />
                            <Form.Check
                              type='radio'
                              id='diplay_multiple-no'
                              name='diplay_multiple'
                              label='No'
                              checked={groupingDetail.diplay_multiple === 0}
                              onChange={() =>
                                setGroupingDetail((prev) => ({...prev, diplay_multiple: 0}))
                              }
                              className='d-flex align-items-center gap-2'
                            />
                          </div>
                        </div>
                      </div>

                      <div className='row pt-5'>
                        <div className='col-lg-12 d-flex justify-content-end gap-3'>
                          <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                            Cancel
                          </Button>
                          <Button
                            variant='primary'
                            type='submit'
                            disabled={isLoadingButton || !isFormValid}
                          >
                            {isLoadingButton ? (
                              <>
                                <Spinner animation='border' size='sm' className='me-2' />
                                Submit
                              </>
                            ) : (
                              'Submit'
                            )}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  ) : (
                    <GroupingsDetailView
                      groupingDetail={groupingDetail}
                    />
                  )
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default GroupingsDetailPage
